/**
 * アップデート配信 `/updates`。
 *
 * カタログ（配信できる版）とロールアウト（実際に配っている作業）を別タブに分ける。
 * 既定はロールアウト側 — 運営が開くのはたいてい「今流している配信の様子を見る」ため。
 */
import * as React from 'react';
import { Link, getRouteApi, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackageCheck, Plus, Rocket } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  Input,
  PageHeader,
  Section,
  StatusBadge,
  Textarea,
  cn,
  formatCount,
  formatDateTime,
  type DataTableColumn,
} from '@mail-sv/ui';
import { RELEASE_SERVICE, ROLLOUT_RING, labelOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { FormDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  ReleaseCatalogEntry,
  ReleaseDescriptorTemplate,
  RolloutRingProgress,
  RolloutSummary,
} from '../types/api.js';

const routeApi = getRouteApi(RID.updates);

/* ------------------------------------------------------------ 記述子の入力 */

/**
 * リリース記述子（`update_releases.descriptor`）の既定の再作成順。
 *
 * 補助サービス（unbound → clamav → rspamd → postsrsd）を先に入れ替え、
 * 配送の本体（dovecot → postfix）を後に回す。**エージェントは最後**
 * ―― 自分自身を先に入れ替えると、残りの入れ替えを見届ける監視が
 * 途中で切れる（失敗しても CP からは「無応答」としか見えない）。
 *
 * 画面はこの順に**並べ替えるだけ**で、`images` に無いサービスは落とす。
 */
const RECOMMENDED_ORDER = [
  'unbound',
  'clamav',
  'rspamd',
  'postsrsd',
  'dovecot',
  'postfix',
  'agent',
] as const;

/**
 * 配信できるコンポーネント。**DB の enum（`update_component`）が正**で、
 * 値は `agent` / `mailnode_stack` の 2 つしかない。
 *
 * 以前ここには `mailnode` と `template` が並んでいて、**選ぶと必ず 400**
 * （画面には「入力内容に誤りがあります」としか出ない）になっていた。
 * `@mail-sv/db` は SPA から import できないので、値はここに写す
 * ―― 増やすときは `packages/db/src/schema/_enums.ts` と必ず突き合わせること。
 */
const UPDATE_COMPONENT_OPTIONS = [
  { value: 'agent', label: 'エージェント' },
  { value: 'mailnode_stack', label: 'メールノード（スタック）' },
];

/** `…@sha256:<64hex>`。**タグ参照はサーバー側でも弾かれる**（保存前に見せる）。 */
const DIGEST_REF = /^[a-z0-9.\-_/:]+@sha256:[a-f0-9]{64}$/;

/** リリース版数。記述子を付けるならこの形でないと配信時に halt する。 */
const RELEASE_VERSION = /^\d{4}\.\d{2}\.\d+$/;

interface DescriptorParse {
  descriptor: ReleaseDescriptorTemplate | null;
  error: string | null;
}

/**
 * `release.yml` の成果物（`release-images.json`）をそのまま貼れるようにする。
 *
 * 受ける形は 2 つ:
 *   - `{"postfix": "ghcr.io/…@sha256:…"}`（サービス名 → イメージ）
 *   - `{"images": { … }}`（`release-images.json` の素の形）
 *
 * **順序は貼り付けからは決めない。** JSON のキー順は生成側の都合で変わるので、
 * `RECOMMENDED_ORDER` で並べ替えたものを既定にし、必要なら手で直させる。
 */
export function parseImagesJson(text: string, orderText: string, notInstalledBefore: string): DescriptorParse {
  const trimmed = text.trim();
  if (trimmed.length === 0) return { descriptor: null, error: null };

  let raw: unknown;
  try {
    raw = JSON.parse(trimmed);
  } catch {
    return { descriptor: null, error: 'JSON として読めません。' };
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { descriptor: null, error: 'オブジェクト（{ … }）を貼り付けてください。' };
  }
  const outer = raw as Record<string, unknown>;
  const inner =
    typeof outer.images === 'object' && outer.images !== null && !Array.isArray(outer.images)
      ? (outer.images as Record<string, unknown>)
      : outer;

  const images: Record<string, string> = {};
  for (const [key, value] of Object.entries(inner)) {
    if (!(RELEASE_SERVICE as readonly string[]).includes(key)) {
      return { descriptor: null, error: `未知のサービス名です: ${key}（${RELEASE_SERVICE.join(' / ')}）` };
    }
    if (typeof value !== 'string' || !DIGEST_REF.test(value)) {
      return {
        descriptor: null,
        error: `${key} のイメージ参照が @sha256:<64桁hex> のダイジェスト固定ではありません。`,
      };
    }
    images[key] = value;
  }
  if (Object.keys(images).length === 0) {
    return { descriptor: null, error: 'イメージが 1 つもありません。' };
  }

  const order =
    orderText.trim().length > 0
      ? orderText
          .split(/[,\s]+/)
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      : RECOMMENDED_ORDER.filter((s) => images[s] !== undefined);
  if (order.length === 0) return { descriptor: null, error: '再作成の順序が空です。' };
  for (const service of order) {
    if (!(RELEASE_SERVICE as readonly string[]).includes(service)) {
      return { descriptor: null, error: `順序に未知のサービス名があります: ${service}` };
    }
    if (images[service] === undefined) {
      return { descriptor: null, error: `${service} のイメージが images にありません。` };
    }
  }

  const before = notInstalledBefore.trim();
  if (before.length > 0 && !RELEASE_VERSION.test(before)) {
    return { descriptor: null, error: '単調性制約は YYYY.MM.N 形式で入力してください。' };
  }

  return {
    descriptor: { images, order, notInstalledBefore: before.length > 0 ? before : null },
    error: null,
  };
}

export function UpdatesPage(): React.ReactElement {
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();

  return (
    <>
      <PageHeader
        title="アップデート配信"
        description="エージェント・メールスタックの版を管理し、リング単位で段階的に配布します。"
      />

      <PageTabs
        value={search.tab}
        onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
        tabs={[
          { value: 'rollouts', label: 'ロールアウト', icon: Rocket },
          { value: 'catalog', label: 'カタログ', icon: PackageCheck },
        ]}
      />

      {search.tab === 'rollouts' ? <RolloutsTab /> : <CatalogTab />}
    </>
  );
}

/* ------------------------------------------------------------- ロールアウト */

function RolloutsTab(): React.ReactElement {
  const canPublish = usePermission(OPS_PERMISSION.updatePublish);
  const [createOpen, setCreateOpen] = React.useState(false);

  const list = useCursorList<RolloutSummary>({
    path: '/updates/rollouts',
    keyFor: (params) => qk.rollouts(params),
    filters: {},
    refetchInterval: 5_000,
  });

  const columns: Array<DataTableColumn<RolloutSummary>> = [
    {
      id: 'target',
      header: '対象',
      cell: (r) => (
        <Link to="/updates/rollouts/$rolloutId" params={{ rolloutId: r.id }} className="font-medium hover:text-primary">
          {r.component} <span className="tabular text-muted-foreground">{r.version}</span>
        </Link>
      ),
    },
    { id: 'status', header: '状態', cell: (r) => <StatusBadge status={r.status} size="sm" dot pulse={r.status === 'running'} /> },
    { id: 'progress', header: '進行', width: 'w-72', cell: (r) => <RingProgress rings={r.rings} /> },
    {
      id: 'result',
      header: '成功/失敗',
      align: 'right',
      hideBelow: 'md',
      cell: (r) => {
        const ok = r.rings.reduce((s, x) => s + x.succeeded, 0);
        const ng = r.rings.reduce((s, x) => s + x.failed, 0);
        return (
          <span className="tabular text-sm">
            <span className="text-success">{formatCount(ok)}</span>
            {' / '}
            <span className={ng > 0 ? 'text-destructive' : undefined}>{formatCount(ng)}</span>
          </span>
        );
      },
    },
    { id: 'startedBy', header: '開始者', hideBelow: 'lg', cell: (r) => r.startedBy },
    { id: 'startedAt', header: '開始日時', hideBelow: 'md', cell: (r) => formatDateTime(r.startedAt) },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-end gap-2">
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        {canPublish ? (
          /*
           * **「開始」ではなく「作成」。** `POST /updates/rollouts` は
           * `status='draft'` で作るだけで、実際に配り始めるのは詳細画面の
           * `advance`（＝配信を開始）を押したとき。ボタンに「開始」と書いていた
           * ころは、押しても何も配られないのに「開始した」と読める表示になっていた。
           */
          <Button onClick={() => setCreateOpen(true)}>
            <Plus aria-hidden />
            ロールアウトを作成
          </Button>
        ) : null}
      </div>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(r) => r.id}
        loading={list.query.isLoading}
        emptyTitle="ロールアウトはまだありません"
        emptyDescription="カタログに版を登録してから配信を開始します。"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      <CreateRolloutDialog open={createOpen} onOpenChange={setCreateOpen} />
    </>
  );
}

/** リングごとの進捗バー。canary → ring1 → ring2 の順に並べる。 */
export function RingProgress({ rings }: { rings: RolloutRingProgress[] }): React.ReactElement {
  const order = new Map(ROLLOUT_RING.map((r, i) => [r, i]));
  const sorted = [...rings].sort((a, b) => (order.get(a.ring) ?? 9) - (order.get(b.ring) ?? 9));
  return (
    <div className="space-y-1">
      {sorted.map((r) => {
        const done = r.succeeded + r.failed;
        const ratio = r.total > 0 ? done / r.total : 0;
        return (
          <div key={r.ring} className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-[10px] text-muted-foreground">{labelOf(r.ring)}</span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
              <div
                className={cn('h-full rounded-full', r.failed > 0 ? 'bg-destructive' : 'bg-primary')}
                style={{ width: `${ratio * 100}%` }}
              />
            </div>
            <span className="w-14 shrink-0 text-right text-[10px] tabular text-muted-foreground">
              {done}/{r.total}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function CreateRolloutDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [catalogId, setCatalogId] = React.useState('');
  const [canarySoak, setCanarySoak] = React.useState('60');
  const [ring1Soak, setRing1Soak] = React.useState('120');
  const [maxUnavailable, setMaxUnavailable] = React.useState('10');

  const catalog = useQuery({
    queryKey: qk.updateCatalog(),
    queryFn: () => api.get<{ items: ReleaseCatalogEntry[] }>('/updates/catalog'),
    enabled: open,
  });

  const mut = useMutation({
    mutationFn: () =>
      api.post<{ id: string; status: string }>('/updates/rollouts', {
        catalogId,
        rings: ROLLOUT_RING,
        soakMinutes: { canary: Number(canarySoak), ring1: Number(ring1Soak) },
        maxUnavailablePct: Number(maxUnavailable),
      }),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: qk.rollouts() });
      onOpenChange(false);
      // draft のまま一覧へ戻すと「作ったのに何も起きない」ので、
      // 開始ボタンのある詳細へそのまま送る。
      void navigate({ to: '/updates/rollouts/$rolloutId', params: { rolloutId: created.id } });
    },
  });

  /*
   * **公開済みだけを候補にする絞り込みは画面側で行う。**
   * `GET /ops/updates/catalog` はクエリを一切解釈しない（200 件を新しい順に
   * 返すだけ）ので、`{state:'published'}` を付けても取り下げ済みの版まで
   * 並んだままになり、選ぶと作成が 400（取り下げ済みのリリースは配信できません）
   * で弾かれる。サーバーが絞ってくれる体で書かない。
   *
   * **記述子の無い版も候補から外す。** `descriptor` が `null` の版は
   * `POST /updates/rollouts` が 400 で止める（以前は作成が通ったうえで、
   * 1 台目の配布で「リリース記述子が不正です」と halt していた）。
   */
  const published = (catalog.data?.items ?? []).filter((c) => c.state === 'published');
  const deliverable = published.filter((c) => c.descriptor !== null);
  const missingDescriptor = published.length - deliverable.length;
  const options = deliverable.map((c) => ({
    value: c.id,
    label: `${c.component} ${c.version}`,
  }));

  React.useEffect(() => {
    if (open && options.length > 0 && catalogId === '') setCatalogId(options[0]?.value ?? '');
  }, [open, options, catalogId]);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="ロールアウトを作成"
      description="canary → ring1 → ring2 の順に配信します。作成した時点では下書き（draft）で、詳細画面の「配信を開始」を押すまで 1 台にも配りません。soak 中にヘルスゲートが落ちると自動で halt します。"
      submitLabel="作成する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={catalogId === ''}
      onSubmit={() => mut.mutate()}
    >
      {missingDescriptor > 0 ? (
        <Alert variant="warning">
          <AlertTitle>記述子の無い版が {missingDescriptor} 件あります</AlertTitle>
          <AlertDescription>
            イメージ（images）と再作成順（order）が登録されていない版は配れないので、候補から外しています。
            カタログの「バージョンを登録」でイメージを入れ直してください。
          </AlertDescription>
        </Alert>
      ) : null}

      <Field label="配信する版" required>
        <NativeSelect
          value={catalogId}
          onChange={setCatalogId}
          options={options.length > 0 ? options : [{ value: '', label: '配信できる版がありません' }]}
        />
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="canary の soak（分）">
          <Input type="number" min={0} value={canarySoak} onChange={(e) => setCanarySoak(e.target.value)} />
        </Field>
        <Field label="ring1 の soak（分）">
          <Input type="number" min={0} value={ring1Soak} onChange={(e) => setRing1Soak(e.target.value)} />
        </Field>
        <Field label="同時停止の上限（%）">
          <Input type="number" min={1} max={100} value={maxUnavailable} onChange={(e) => setMaxUnavailable(e.target.value)} />
        </Field>
      </div>
    </FormDialog>
  );
}

/* ------------------------------------------------------------------ カタログ */

function CatalogTab(): React.ReactElement {
  const canPublish = usePermission(OPS_PERMISSION.updatePublish);
  const [registerOpen, setRegisterOpen] = React.useState(false);
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const list = useCursorList<ReleaseCatalogEntry>({
    path: '/updates/catalog',
    keyFor: (params) => qk.updateCatalog(params),
    filters: {},
  });

  const columns: Array<DataTableColumn<ReleaseCatalogEntry>> = [
    { id: 'component', header: 'コンポーネント', cell: (c) => <span className="font-medium">{c.component}</span> },
    { id: 'version', header: 'バージョン', cell: (c) => <span className="tabular">{c.version}</span> },
    { id: 'channel', header: 'チャネル', hideBelow: 'md', cell: (c) => <Badge variant="secondary" size="sm">{c.channel}</Badge> },
    { id: 'state', header: '状態', cell: (c) => <StatusBadge status={c.state} size="sm" /> },
    {
      id: 'descriptor',
      header: '記述子',
      // **「登録されている」と「配れる」は別。** 記述子の無い版は台帳に
      // 載るだけでロールアウトを作れない（作成が 400 になる）。
      cell: (c) =>
        c.descriptor ? (
          <Badge variant="success" size="sm">
            {c.descriptor.order.length} サービス
          </Badge>
        ) : (
          <Badge variant="warning" size="sm">
            未登録（配れません）
          </Badge>
        ),
    },
    { id: 'published', header: '公開日', hideBelow: 'md', cell: (c) => formatDateTime(c.publishedAt) },
    {
      id: 'notes',
      header: 'リリースノート',
      cell: (c) =>
        c.releaseNotes ? (
          <button
            type="button"
            className="text-xs text-primary hover:underline"
            onClick={() => setExpanded((prev) => (prev === c.id ? null : c.id))}
          >
            {expanded === c.id ? '閉じる' : '表示'}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  const expandedEntry = list.rows.find((c) => c.id === expanded) ?? null;

  return (
    <>
      <div className="mb-4 flex items-center justify-end gap-2">
        <RefetchingDot active={list.query.isFetching && !list.query.isLoading} />
        {canPublish ? <Button onClick={() => setRegisterOpen(true)}>バージョンを登録</Button> : null}
      </div>

      {list.query.isError ? (
        <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} className="mb-4" />
      ) : null}

      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(c) => c.id}
        loading={list.query.isLoading}
        emptyTitle="登録された版がありません"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />

      {expandedEntry?.releaseNotes ? (
        <Section title={`${expandedEntry.component} ${expandedEntry.version} のリリースノート`} className="mt-6">
          <div className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-sm">
            {expandedEntry.releaseNotes}
          </div>
          {expandedEntry.sha256 ? (
            <p className="mt-2 break-all font-mono text-xs text-muted-foreground">sha256: {expandedEntry.sha256}</p>
          ) : null}
        </Section>
      ) : null}

      <RegisterVersionDialog open={registerOpen} onOpenChange={setRegisterOpen} />
    </>
  );
}

function RegisterVersionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [form, setForm] = React.useState({
    component: 'agent',
    version: '',
    artifactUrl: '',
    sha256: '',
    releaseNotes: '',
    minAgentVersion: '',
    channel: 'stable',
    severity: 'normal',
    requiresMigration: false,
    /** `release-images.json` をそのまま貼る欄。 */
    imagesJson: '',
    /** 空なら `RECOMMENDED_ORDER` から自動で決める。 */
    order: '',
    notInstalledBefore: '',
  });

  const parsed = parseImagesJson(form.imagesJson, form.order, form.notInstalledBefore);

  const mut = useMutation({
    mutationFn: () =>
      api.post('/updates/catalog', {
        component: form.component,
        version: form.version.trim(),
        artifactUrl: form.artifactUrl.trim(),
        sha256: form.sha256.trim(),
        releaseNotes: form.releaseNotes,
        minAgentVersion: form.minAgentVersion.trim() || null,
        channel: form.channel,
        severity: form.severity,
        requiresMigration: form.requiresMigration,
        // 空欄なら送らない（サーバー側は optional。列は `{}` のまま＝配れない版）。
        ...(parsed.descriptor ? { descriptor: parsed.descriptor } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.updateCatalog() });
      onOpenChange(false);
    },
  });

  /*
   * **`artifactUrl` はサーバー側で必須**（`z.string().min(1)`）。
   * 以前はここで空欄のまま送れたので、URL を入れ忘れた登録が必ず 400 で
   * 落ちていた（画面には「入力が不正です」としか出ず、どの欄かは分からない）。
   * sha256 は 64 桁の 16 進で、エージェントが取得後に必ず照合する。
   */
  const valid =
    form.version.trim().length > 0 &&
    form.artifactUrl.trim().length > 0 &&
    /^[0-9a-f]{64}$/.test(form.sha256.trim()) &&
    parsed.error === null &&
    // 記述子を付けるなら版数は `YYYY.MM.N`。守らないと配信時に
    // `releaseVersionSchema` が弾いてロールアウトが halt する。
    (parsed.descriptor === null || RELEASE_VERSION.test(form.version.trim()));

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="バージョンを登録"
      description="登録した時点で公開（published）になり、ロールアウトの対象に選べるようになります。配るかどうかはロールアウトの作成で決めます。"
      submitLabel="登録する"
      pending={mut.isPending}
      error={mut.isError ? mut.error : undefined}
      disabled={!valid}
      onSubmit={() => mut.mutate()}
      wide
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="コンポーネント" required>
          {/*
            **語彙は DB の enum（`update_component`）が正**で、値は
            `agent` / `mailnode_stack` の 2 つしかない。以前ここには
            `mailnode` と `template` が並んでいて、**選ぶと必ず 400**
            （「入力内容に誤りがあります」としか出ない）になっていた。
          */}
          <NativeSelect
            value={form.component}
            onChange={(v) => setForm({ ...form, component: v })}
            options={UPDATE_COMPONENT_OPTIONS}
          />
        </Field>
        <Field label="バージョン" required hint="semver。既存と重複すると 409 になります。">
          <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} placeholder="1.4.2" />
        </Field>
        <Field label="アーティファクト URL" required hint="エージェントが取得する配布物の場所。">
          <Input value={form.artifactUrl} onChange={(e) => setForm({ ...form, artifactUrl: e.target.value })} />
        </Field>
        <Field label="SHA-256" required hint="64 桁の 16 進数。エージェントが取得後に必ず照合します。">
          <Input
            value={form.sha256}
            onChange={(e) => setForm({ ...form, sha256: e.target.value })}
            className="font-mono text-xs"
          />
        </Field>
        <Field label="チャネル" hint="dev / beta は検証用。既定は stable。">
          <NativeSelect
            value={form.channel}
            onChange={(v) => setForm({ ...form, channel: v })}
            options={[
              { value: 'stable', label: 'stable（本番）' },
              { value: 'beta', label: 'beta（先行）' },
              { value: 'dev', label: 'dev（開発）' },
            ]}
          />
        </Field>
        <Field label="重要度" hint="security はリングを飛ばして急ぐ判断の材料になります。">
          <NativeSelect
            value={form.severity}
            onChange={(v) => setForm({ ...form, severity: v })}
            options={[
              { value: 'normal', label: '通常' },
              { value: 'important', label: '重要' },
              { value: 'security', label: 'セキュリティ' },
            ]}
          />
        </Field>
        <Field label="必要な最小エージェント版" className="sm:col-span-2">
          <Input value={form.minAgentVersion} onChange={(e) => setForm({ ...form, minAgentVersion: e.target.value })} />
        </Field>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={form.requiresMigration}
            onChange={(e) => setForm({ ...form, requiresMigration: e.target.checked })}
            className="size-4 rounded border-border"
          />
          ノード DB のマイグレーションを伴う（適用に時間がかかり、切り戻しが難しくなります）
        </label>
        <Field label="リリースノート" className="sm:col-span-2">
          <Textarea rows={5} value={form.releaseNotes} onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })} />
        </Field>

        {/*
          ------------------------------------------------------------------
          リリース記述子。**ここが空だとその版は 1 台にも配れない。**
          ------------------------------------------------------------------
          worker は配信のたびに `{...descriptor, version, nodeId, issuedAt,
          expiresAt}` を検証してから Ed25519 で署名する。以前はこの欄が
          存在せず、列は既定の `{}` のままだったので safeParse が必ず失敗し、
          1 台目の配布で「リリース記述子が不正です」と halt していた
          （＝リング配信・soak・自動ロールバックの一式が到達不能だった）。
        */}
        <div className="sm:col-span-2 rounded-lg border border-border bg-panel p-4">
          <p className="mb-1 text-sm font-medium">リリース記述子（配るために必須）</p>
          <p className="mb-3 text-xs text-muted-foreground">
            ここが空のまま登録した版は台帳に載るだけで、ロールアウトを作れません
            （作成時に 400 で止まります）。CI の成果物 <code className="font-mono">release-images.json</code>{' '}
            をそのまま貼り付けてください。
          </p>

          <div className="space-y-4">
            <Field
              label="イメージ（JSON）"
              error={parsed.error ?? undefined}
              hint={`サービス名 → @sha256 固定のイメージ参照。使えるキー: ${RELEASE_SERVICE.join(' / ')}`}
            >
              <Textarea
                rows={6}
                value={form.imagesJson}
                onChange={(e) => setForm({ ...form, imagesJson: e.target.value })}
                className="font-mono text-xs"
                spellCheck={false}
                placeholder={'{\n  "postfix": "ghcr.io/…/postfix@sha256:…",\n  "dovecot": "ghcr.io/…/dovecot@sha256:…"\n}'}
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="再作成の順序"
                hint="空欄なら補助サービス → 配送本体 → エージェントの順に自動で並べます。"
              >
                <Input
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: e.target.value })}
                  className="font-mono text-xs"
                  spellCheck={false}
                  placeholder={parsed.descriptor?.order.join(', ') ?? 'rspamd, dovecot, postfix'}
                />
              </Field>
              <Field
                label="この版数未満には配らない"
                hint="YYYY.MM.N。空欄なら制約なし。古い版を新しい世代で押し込む攻撃を防ぎます。"
              >
                <Input
                  value={form.notInstalledBefore}
                  onChange={(e) => setForm({ ...form, notInstalledBefore: e.target.value })}
                  className="font-mono text-xs"
                  placeholder="2026.08.1"
                />
              </Field>
            </div>

            {parsed.descriptor ? (
              <Alert>
                <AlertTitle>
                  {Object.keys(parsed.descriptor.images).length} サービスを次の順に入れ替えます
                </AlertTitle>
                <AlertDescription>
                  <span className="font-mono text-xs">{parsed.descriptor.order.join(' → ')}</span>
                </AlertDescription>
              </Alert>
            ) : null}

            {parsed.descriptor && !RELEASE_VERSION.test(form.version.trim()) ? (
              <Alert variant="warning">
                <AlertTitle>記述子を付けるならバージョンは YYYY.MM.N 形式です</AlertTitle>
                <AlertDescription>
                  配信時にこの版数がそのまま記述子へ写ります。形式が違うとノードへ配る直前に
                  ロールアウトが halt します。
                </AlertDescription>
              </Alert>
            ) : null}
          </div>
        </div>
      </div>
    </FormDialog>
  );
}
